import React from "react";
import first from "lodash/first";
import isNil from "lodash/isNil";

import { Box, Divider, Typography } from "@mui/material";

import {
  adjustTripIndex,
  CustomerPricingType,
} from "globals/hooks/utils";
import AllTripsPriceSummaryBlock from "./components/AllTripsPriceSummaryBlock";
import ActiveTripPricingBlock from "./components/ActiveTripPricingBlock";
import { useOperator, useOperatorCurrency } from "globals/hooks";
import PromoCodeBlock from "./components/PromoCodeBlock";

type PriceOverviewBlockProps = {
  pricings: CustomerPricingType[];
  orderTotalAmt: number;
  activeTripIndex?: number;
  pickUpDateTime?: string;
  isCharter?: boolean;
};

function PriceOverviewBlock(props: PriceOverviewBlockProps) {
  const {
    pricings,
    orderTotalAmt,
    activeTripIndex = 0,
    pickUpDateTime,
    isCharter = false,
  } = props;

  // hooks
  const {
    operator: {
      settings: { hasActivePromoCodes, currencyCode } = {},
    },
  } = useOperator();
  const currency = useOperatorCurrency();
  const indexedPricings = adjustTripIndex(pricings);

  const shouldRenderSummary =
    !isCharter && (pricings.length > 1 || first(pricings).returnTripPricing);
  const activeTripPricing = indexedPricings[activeTripIndex];

  // boolean for in any outboundTripPricing or returnTripPricing has promoCode.value
  const pricingsHasPromoCode = indexedPricings.some(
    (pricing) =>
      !isNil(pricing.outboundTripPricing.promoCode.value) ||
      !isNil(pricing.returnTripPricing?.promoCode.value)
  );

  // should render promo code input if operator has active promo codes and the first trip does not have a promo code
  const shouldRenderPromoCodeInput =
    hasActivePromoCodes && !pricingsHasPromoCode;

  return (
    <Box>
      <Box mb={2}>
        <Typography variant="h3" data-testid="price-overview">
          Price Overview
        </Typography>
      </Box>

      {isCharter && (
        <Box
          p={1}
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Typography variant="h4">Reservation Total</Typography>
          <Typography variant="h2">{currency(orderTotalAmt)}</Typography>
        </Box>
      )}

      {!isCharter && (
        <ActiveTripPricingBlock
          pricing={activeTripPricing}
          currencyCode={currencyCode}
        />
      )}
      {shouldRenderPromoCodeInput && (
        <PromoCodeBlock pickUpDateTime={pickUpDateTime} />
      )}

      {shouldRenderSummary && (
        <AllTripsPriceSummaryBlock
          pricings={indexedPricings}
          orderTotalAmt={orderTotalAmt}
          currencyCode={currencyCode}
        />
      )}
      <Box mt={4}>
        <Divider />
      </Box>
    </Box>
  );
}

export default PriceOverviewBlock;
